const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('Missing Supabase environment variables');
}

const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/send-email`;
const STRIPE_CHECKOUT_URL = `/api/create-stripe-checkout`;

export interface NewIntakeEmailData {
  businessOwnerEmail: string;
  applicantName: string;
  selectedPlan: string;
  homeAddress: string;
  homeCondition: string;
}

export interface ApprovalEmailData {
  clientEmail: string;
  clientName: string;
  paymentLink?: string;
}

/**
 * Generates Stripe Checkout payment link for approved application
 */
export async function generatePaymentLink(
  leadId: string,
  clientEmail: string,
  clientName: string,
  planAmount: number
): Promise<{ success: boolean; checkoutUrl?: string; error?: string }> {
  try {
    const response = await fetch(STRIPE_CHECKOUT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        leadId,
        clientEmail,
        clientName,
        planAmount
      })
    });

    const result = await response.json();

    if (!response.ok) {
      return { success: false, error: result.error || 'Failed to generate payment link' };
    }

    return { success: true, checkoutUrl: result.checkoutUrl };
  } catch (error) {
    console.error('Error generating payment link:', error);
    return { success: false, error: 'Network error generating payment link' };
  }
}

/**
 * Sends email notification when new intake form is submitted
 */
export async function sendNewIntakeEmail(data: NewIntakeEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        emailType: 'new_intake',
        data
      })
    });

    const result = await response.json();

    if (!response.ok) {
      return { success: false, error: result.error || 'Failed to send email' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending new intake email:', error);
    return { success: false, error: 'Network error sending email' };
  }
}

/**
 * Sends email notification when application is approved
 */
export async function sendApprovalEmail(data: ApprovalEmailData): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        emailType: 'approval_notification',
        data
      })
    });

    const result = await response.json();

    if (!response.ok) {
      return { success: false, error: result.error || 'Failed to send email' };
    }

    return { success: true };
  } catch (error) {
    console.error('Error sending approval email:', error);
    return { success: false, error: 'Network error sending email' };
  }
}